import {
  TOPUP_CONFIG_REFETCH_INTERVAL_MS,
  useTopupConfig,
} from '@/src/api/payg';
import { BannerConfig } from '@/src/api/payg/types';
import { PAYG_EVENTS } from '@/src/constants/analytics';
import AnalyticsService from '@/src/services/analyticsService';
import { getErrorDetails } from '@/src/utils/errorUtils';
import { useEffect, useRef } from 'react';

type UseSupportBannerResult = {
  banner: BannerConfig | null;
  disableTopups: boolean;
};

/**
 * Single source of truth for the support banner and the topups-blocked state.
 *
 * Emergency banners and the topups-blocked switch are served through the config,
 * so it is polled for as long as a screen using this hook is mounted — mounting
 * the hook is what opts a page into the refresh. `staleTime` matches the poll
 * window, so a second reader on the same page uses the cache rather than
 * refetching.
 */
export const useSupportBanner = (): UseSupportBannerResult => {
  const { data, error, errorUpdatedAt } = useTopupConfig({
    refetchInterval: TOPUP_CONFIG_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true, // keep polling when the tab is backgrounded
  });

  // A failed poll is silent to the customer — the banner simply doesn't render,
  // so a stale "all clear" is all they see. Report each failure so the gap is
  // visible. Keyed on `errorUpdatedAt` to report once per failed fetch rather
  // than on every re-render while the error persists.
  const lastReportedErrorAt = useRef<number>();

  useEffect(() => {
    if (!error || errorUpdatedAt === lastReportedErrorAt.current) {
      return;
    }
    lastReportedErrorAt.current = errorUpdatedAt;

    const { code, message } = getErrorDetails(error);
    AnalyticsService.dispatch(
      ...PAYG_EVENTS.configUnavailable({ code, message }),
    );
  }, [error, errorUpdatedAt]);

  return {
    banner: data?.banner ?? null,
    disableTopups: data?.topupsBlocked ?? false,
  };
};
